// Dashboard Widget Components
import { TrendingUp, TrendingDown, Users, Shield, AlertTriangle, FileText } from 'lucide-react';
import PropTypes from 'prop-types';
import React from 'react';
import { Line, Bar, Doughnut } from 'react-chartjs-2';

import ProcoreCard from '../ProcoreCard';

// Compliance Score Widget
const ComplianceScoreWidget = ({ data }) => {
  const score = data?.compliancePct || 87;
  const trend = 3;

  return (
    <ProcoreCard className="compact">
      <div className="flex items-center justify-between">
        <div>
          <div className="procore-metric-label">Compliance Score</div>
          <div className="procore-metric-value text-2xl">{score}%</div>
          <div className={`procore-metric-change ${trend > 0 ? 'positive' : 'negative'}`}>
            {trend > 0 ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
            {Math.abs(trend)}% from last week
          </div>
        </div>
        <div className="relative">
          <svg className="transform -rotate-90 w-20 h-20">
            <circle cx="40" cy="40" r="36" stroke="#E5E7EB" strokeWidth="8" fill="none" />
            <circle
              cx="40"
              cy="40"
              r="36"
              stroke="#22C55E"
              strokeWidth="8"
              fill="none"
              strokeDasharray={`${2 * Math.PI * 36}`}
              strokeDashoffset={`${2 * Math.PI * 36 * (1 - score / 100)}`}
            />
          </svg>
          <Shield
            className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-green-600"
            size={24}
          />
        </div>
      </div>
    </ProcoreCard>
  );
};

// Risk Score Trend Widget
const RiskScoreTrendWidget = ({ data: _data }) => {
  const chartData = {
    labels: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
    datasets: [
      {
        label: 'Risk Score',
        data: [72, 70, 68, 69, 65, 67, 65],
        borderColor: '#F47E3E',
        backgroundColor: 'rgba(244, 126, 62, 0.1)',
        tension: 0.3,
      },
    ],
  };

  return (
    <ProcoreCard title="Risk Score Trend" className="compact">
      <div style={{ height: '150px' }}>
        <Line
          data={chartData}
          options={{
            responsive: true,
            maintainAspectRatio: false,
            scales: {
              y: { beginAtZero: true, max: 100 },
            },
            plugins: {
              legend: { display: false },
            },
          }}
        />
      </div>
    </ProcoreCard>
  );
};

// Active Workers Widget
const ActiveWorkersWidget = ({ data }) => {
  const totalWorkers = data?.workers?.length || 45;
  const activeToday = 38;

  return (
    <ProcoreCard className="compact">
      <div className="flex items-center justify-between">
        <div>
          <div className="procore-metric-label">Active Workers</div>
          <div className="procore-metric-value text-2xl">
            {activeToday}/{totalWorkers}
          </div>
          <div className="text-sm text-gray-600 mt-1">On site today</div>
        </div>
        <Users className="text-blue-500" size={32} />
      </div>
    </ProcoreCard>
  );
};

// Open Hazards Widget
const OpenHazardsWidget = ({ data: _data }) => {
  const hazards = {
    critical: 2,
    high: 5,
    medium: 8,
    low: 3,
  };

  return (
    <ProcoreCard className="compact">
      <div className="procore-metric-label mb-3">Open Hazards</div>
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <span className="text-xs text-red-600 font-medium">Critical</span>
          <span className="font-bold text-red-600">{hazards.critical}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-xs text-orange-600 font-medium">High</span>
          <span className="font-bold text-orange-600">{hazards.high}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-xs text-yellow-600 font-medium">Medium</span>
          <span className="font-bold text-yellow-600">{hazards.medium}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-xs text-green-600 font-medium">Low</span>
          <span className="font-bold text-green-600">{hazards.low}</span>
        </div>
      </div>
    </ProcoreCard>
  );
};

// Training Status Widget
const TrainingStatusWidget = ({ data: _data }) => {
  const completion = 82;

  return (
    <ProcoreCard className="compact">
      <div className="procore-metric-label mb-3">Training Status</div>
      <div className="text-center">
        <div className="text-3xl font-bold text-gray-900">{completion}%</div>
        <div className="text-sm text-gray-600">Completed</div>
        <div className="mt-3 bg-gray-200 rounded-full h-2">
          <div
            className="bg-purple-500 h-2 rounded-full transition-all duration-500"
            style={{ width: '82%' }}
          />
        </div>
        <div className="mt-2 text-xs text-gray-500">37 of 45 workers</div>
      </div>
    </ProcoreCard>
  );
};

// Incident Rate Widget
const IncidentRateWidget = ({ data: _data }) => {
  const currentMonth = 3;
  const lastMonth = 5;
  const change = (((currentMonth - lastMonth) / lastMonth) * 100).toFixed(0);

  return (
    <ProcoreCard className="compact">
      <div className="flex items-center justify-between">
        <div>
          <div className="procore-metric-label">Incident Rate</div>
          <div className="procore-metric-value text-2xl">{currentMonth}</div>
          <div className="procore-metric-change positive">
            <TrendingDown size={14} />
            {Math.abs(change)}% from last month
          </div>
        </div>
        <AlertTriangle className="text-yellow-500" size={32} />
      </div>
    </ProcoreCard>
  );
};

// Upcoming Inspections Widget
const UpcomingInspectionsWidget = ({ data: _data }) => {
  const inspections = [
    { name: 'Electrical Safety', due: '2 days' },
    { name: 'Equipment Check', due: '5 days' },
    { name: 'Site Walkthrough', due: '1 week' },
  ];

  return (
    <ProcoreCard title="Upcoming Inspections" className="compact">
      <div className="space-y-2">
        {inspections.map((inspection, index) => (
          <div
            key={`inspection-${inspection.id || inspection.name || index}`}
            className="flex items-center justify-between py-1"
          >
            <span className="text-sm text-gray-700">{inspection.name}</span>
            <span className="text-xs text-gray-500">{inspection.due}</span>
          </div>
        ))}
      </div>
    </ProcoreCard>
  );
};

// Equipment Status Widget
const EquipmentStatusWidget = ({ data: _data }) => {
  const equipment = {
    certified: 24,
    expiring: 5,
    expired: 2,
  };

  return (
    <ProcoreCard className="compact">
      <div className="procore-metric-label mb-3">Equipment Status</div>
      <div className="flex items-center justify-around">
        <div className="text-center">
          <div className="text-2xl font-bold text-green-600">{equipment.certified}</div>
          <div className="text-xs text-gray-500">Certified</div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-yellow-600">{equipment.expiring}</div>
          <div className="text-xs text-gray-500">Expiring</div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-red-600">{equipment.expired}</div>
          <div className="text-xs text-gray-500">Expired</div>
        </div>
      </div>
    </ProcoreCard>
  );
};

// Safety Meetings Widget
const SafetyMeetingsWidget = ({ data: _data }) => {
  const attendance = 92;

  return (
    <ProcoreCard className="compact">
      <div className="flex items-center justify-between">
        <div>
          <div className="procore-metric-label">Safety Meetings</div>
          <div className="procore-metric-value text-2xl">{attendance}%</div>
          <div className="text-sm text-gray-600 mt-1">Attendance rate</div>
        </div>
        <FileText className="text-gray-400" size={32} />
      </div>
    </ProcoreCard>
  );
};

// Certification Expiry Widget
const CertificationExpiryWidget = ({ data: _data }) => {
  const expiring = [
    { name: 'John Smith - OSHA', days: 5 },
    { name: 'Maria Garcia - First Aid', days: 12 },
    { name: 'David Chen - Forklift', days: 18 },
  ];

  return (
    <ProcoreCard title="Expiring Certifications" className="compact">
      <div className="space-y-2">
        {expiring.map((cert, index) => (
          <div
            key={`cert-expiring-${cert.id || cert.name || index}`}
            className="flex items-center justify-between py-1"
          >
            <span className="text-sm text-gray-700 truncate flex-1 mr-2">{cert.name}</span>
            <span
              className={`text-xs font-medium ${
                cert.days < 7
                  ? 'text-red-600'
                  : cert.days < 14
                    ? 'text-yellow-600'
                    : 'text-gray-500'
              }`}
            >
              {cert.days} days
            </span>
          </div>
        ))}
      </div>
    </ProcoreCard>
  );
};

// Jobsite Comparison Widget
const JobsiteComparisonWidget = ({ data: _data }) => {
  const chartData = {
    labels: ['Main Site', 'Warehouse', 'Office'],
    datasets: [
      {
        label: 'Safety Score',
        data: [88, 92, 85],
        backgroundColor: ['#F47E3E', '#3B82F6', '#22C55E'],
      },
    ],
  };

  return (
    <ProcoreCard title="Jobsite Safety Scores" className="compact">
      <div style={{ height: '150px' }}>
        <Bar
          data={chartData}
          options={{
            responsive: true,
            maintainAspectRatio: false,
            scales: {
              y: { beginAtZero: true, max: 100 },
            },
            plugins: {
              legend: { display: false },
            },
          }}
        />
      </div>
    </ProcoreCard>
  );
};

// Compliance Breakdown Widget
const ComplianceBreakdownWidget = ({ data: _data }) => {
  const chartData = {
    labels: ['Safety', 'Training', 'Documentation', 'Equipment'],
    datasets: [
      {
        data: [92, 85, 88, 90],
        backgroundColor: ['#22C55E', '#3B82F6', '#F59E0B', '#EF4444'],
      },
    ],
  };

  return (
    <ProcoreCard title="Compliance by Category" className="compact">
      <div style={{ height: '200px' }}>
        <Doughnut
          data={chartData}
          options={{
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: {
                position: 'right',
                labels: {
                  boxWidth: 12,
                  font: { size: 11 },
                },
              },
            },
          }}
        />
      </div>
    </ProcoreCard>
  );
};

// Additional widgets...
const ProductivityScoreWidget = ({ data: _data }) => {
  return (
    <ProcoreCard className="compact">
      <div className="procore-metric-label">Productivity Score</div>
      <div className="procore-metric-value text-2xl">8.7/10</div>
      <div className="text-sm text-gray-600">Above target</div>
    </ProcoreCard>
  );
};

const SafetyAwardsWidget = ({ data: _data }) => {
  return (
    <ProcoreCard className="compact">
      <div className="procore-metric-label">Safety Awards</div>
      <div className="procore-metric-value text-2xl">12</div>
      <div className="text-sm text-gray-600">Days without incident</div>
    </ProcoreCard>
  );
};

const BudgetTrackingWidget = ({ data: _data }) => {
  const spent = 67;
  return (
    <ProcoreCard className="compact">
      <div className="procore-metric-label">Safety Budget</div>
      <div className="procore-metric-value text-2xl">{spent}%</div>
      <div className="text-sm text-gray-600">Budget utilized</div>
    </ProcoreCard>
  );
};

const AuditScheduleWidget = ({ data: _data }) => {
  return (
    <ProcoreCard className="compact">
      <div className="procore-metric-label">Next Audit</div>
      <div className="procore-metric-value text-xl">Feb 15</div>
      <div className="text-sm text-gray-600">Full compliance audit</div>
    </ProcoreCard>
  );
};

// PropTypes for all dashboard widgets
ComplianceScoreWidget.propTypes = {
  data: PropTypes.shape({
    compliancePct: PropTypes.number,
  }),
};

RiskScoreTrendWidget.propTypes = {
  data: PropTypes.object,
};

ActiveWorkersWidget.propTypes = {
  data: PropTypes.shape({
    workers: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        name: PropTypes.string,
        active: PropTypes.bool,
      })
    ),
  }),
};

OpenHazardsWidget.propTypes = {
  data: PropTypes.object,
};

TrainingStatusWidget.propTypes = {
  data: PropTypes.object,
};

IncidentRateWidget.propTypes = {
  data: PropTypes.object,
};

UpcomingInspectionsWidget.propTypes = {
  data: PropTypes.object,
};

EquipmentStatusWidget.propTypes = {
  data: PropTypes.object,
};

SafetyMeetingsWidget.propTypes = {
  data: PropTypes.object,
};

CertificationExpiryWidget.propTypes = {
  data: PropTypes.object,
};

JobsiteComparisonWidget.propTypes = {
  data: PropTypes.object,
};

ComplianceBreakdownWidget.propTypes = {
  data: PropTypes.object,
};

ProductivityScoreWidget.propTypes = {
  data: PropTypes.object,
};

SafetyAwardsWidget.propTypes = {
  data: PropTypes.object,
};

BudgetTrackingWidget.propTypes = {
  data: PropTypes.object,
};

AuditScheduleWidget.propTypes = {
  data: PropTypes.object,
};

// Export all widgets
export {
  ComplianceScoreWidget,
  RiskScoreTrendWidget,
  ActiveWorkersWidget,
  OpenHazardsWidget,
  TrainingStatusWidget,
  IncidentRateWidget,
  UpcomingInspectionsWidget,
  EquipmentStatusWidget,
  SafetyMeetingsWidget,
  CertificationExpiryWidget,
  JobsiteComparisonWidget,
  ComplianceBreakdownWidget,
  ProductivityScoreWidget,
  SafetyAwardsWidget,
  BudgetTrackingWidget,
  AuditScheduleWidget,
};
